import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle, faTimes, faCheck, faTerminal } from '@fortawesome/free-solid-svg-icons';
import { SolutionValidationBadge } from './SolutionValidationBadge';

interface CommandConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  description: string;
  commands: string[];
  risks?: string[];
  benefits?: string[];
  estimatedDowntime?: string;
  isExecuting?: boolean;
  validation?: any;
}

export const CommandConfirmationModal: React.FC<CommandConfirmationModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  description,
  commands,
  risks = [],
  benefits = [],
  estimatedDowntime,
  isExecuting = false,
  validation
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-orange-50 to-red-50 border-b border-orange-200 px-4 py-3">
          <div className="flex items-center justify-between">
            <h3 className="text-base font-bold text-gray-900 flex items-center">
              <FontAwesomeIcon icon={faExclamationTriangle} className="text-orange-600 mr-2" />
              Confirm Action
            </h3>
            {!isExecuting && (
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="px-4 py-3 max-h-[calc(90vh-140px)] overflow-y-auto">
          {/* Solution Title */}
          <div className="bg-blue-50 border border-blue-200 rounded p-3 mb-3">
            <h4 className="text-sm font-bold text-blue-900 mb-1">{title}</h4>
            <p className="text-xs text-blue-800">{description}</p>
            {estimatedDowntime && (
              <div className="mt-2 text-xs text-blue-700">
                <strong>Estimated Downtime:</strong> {estimatedDowntime}
              </div>
            )}
          </div>

          {/* Solution Validation Badge */}
          {validation && (
            <div className="mb-3">
              <SolutionValidationBadge validation={validation} />
            </div>
          )}

          {/* Commands to be executed */}
          <div className="mb-3">
            <h5 className="text-sm font-semibold text-gray-900 mb-2 flex items-center">
              <FontAwesomeIcon icon={faTerminal} className="mr-1.5 text-green-600" />
              Commands to Execute:
            </h5>
            <div className="bg-gray-900 rounded p-3 space-y-2">
              {commands.map((cmd, idx) => (
                <div key={idx} className="font-mono text-xs text-green-400">
                  <span className="text-gray-500">$ </span>
                  {cmd}
                </div>
              ))}
            </div>
          </div>

          {/* Risks */}
          {risks.length > 0 && (
            <div className="bg-yellow-50 border border-yellow-200 rounded p-3 mb-3">
              <h5 className="text-xs font-semibold text-yellow-900 mb-1.5">⚠️ Risks:</h5>
              <ul className="space-y-1">
                {risks.map((risk, idx) => (
                  <li key={idx} className="text-xs text-yellow-800 flex items-start">
                    <span className="mr-1">•</span>
                    <span>{risk}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Benefits */}
          {benefits.length > 0 && (
            <div className="bg-green-50 border border-green-200 rounded p-3 mb-3">
              <h5 className="text-xs font-semibold text-green-900 mb-1.5">✅ Benefits:</h5>
              <ul className="space-y-1">
                {benefits.map((benefit, idx) => (
                  <li key={idx} className="text-xs text-green-800 flex items-start">
                    <span className="mr-1">•</span>
                    <span>{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Warning */}
          <div className="bg-orange-50 border-l-4 border-orange-500 p-3 rounded">
            <p className="text-xs text-orange-900">
              <strong>⚠️ Important:</strong> By clicking "Execute", you authorize the system to run these AWS CLI commands on your cluster.
              Please review the commands carefully before proceeding.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="bg-gray-50 border-t border-gray-200 px-4 py-3 flex justify-end space-x-2">
          <button
            onClick={onClose}
            disabled={isExecuting}
            className={`px-4 py-2 text-xs border border-gray-300 text-gray-700 rounded hover:bg-gray-100 transition-colors ${
              isExecuting ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={isExecuting}
            className={`px-4 py-2 text-xs text-white rounded transition-colors flex items-center ${
              isExecuting
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-orange-600 hover:bg-orange-700'
            }`}
          >
            <FontAwesomeIcon icon={faCheck} className="mr-1.5" />
            {isExecuting ? 'Executing...' : 'Execute Commands'}
          </button>
        </div>
      </div>
    </div>
  );
};
